import { pascalCase } from 'change-case';

import {
  ABTestRule,
  ABTestRules,
  ABTestRulesNames,
  ABTestService,
  ExperimentName,
  ExperimentVariantName,
} from './ab-test.types.js';

function isVariantValid(variant: ExperimentVariantName, rule: ABTestRule): boolean {
  if (variant === rule.defaultVariant) {
    return true;
  }
  return Object.keys(rule.variants).includes(variant);
}

export function getABTestQueryParamName<E extends ExperimentName>(experiment: E): string {
  return `exp-${experiment}`;
}

export function parseABTestQueryParamOverrides<R extends ABTestRules>(
  query: URLSearchParams,
  rules: R,
): Partial<Record<ABTestRulesNames<R>, string>> {
  const overrides: Partial<Record<ABTestRulesNames<R>, string>> = {};

  for (const experiment of Object.keys(rules).map((e) => e as ABTestRulesNames<R>)) {
    const rule = rules[experiment];
    const queryParamName = getABTestQueryParamName(experiment);
    const variant = query.get(queryParamName);
    if (variant == null) {
      continue;
    }
    if (isVariantValid(variant, rule)) {
      overrides[experiment] = variant;
    }
  }

  return overrides;
}

export function getABTestHeaderName<E extends ExperimentName>(experiment: E): string {
  return `Experiment-${experiment}`;
}

export function parseABTestHeaders<R extends ABTestRules>(
  headers: Record<string, string | string[] | undefined>,
  rules: ABTestRules,
): Partial<Record<ABTestRulesNames<R>, string>> {
  const experiments: Partial<Record<ABTestRulesNames<R>, string>> = {};

  for (const experiment of Object.keys(rules).map((e) => e as ABTestRulesNames<R>)) {
    const rule = rules[experiment];
    const headerName = getABTestHeaderName(experiment);
    // Express may convert the header name to lower ca se. so we should check for both
    const variant = headers[headerName] ?? headers[headerName.toLowerCase()];
    if (variant == null || typeof variant != 'string') {
      continue;
    }
    if (isVariantValid(variant, rule)) {
      experiments[experiment] = variant;
    }
  }

  return experiments;
}

export function createABTestHeaders<E extends ExperimentName>(
  userExperiments: Record<E, ExperimentVariantName>,
): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const [exp, variant] of Object.entries(userExperiments)) {
    headers[getABTestHeaderName(exp as E)] = variant as string;
  }
  return headers;
}

export function getABTestAnalyticsProperties<R extends ABTestRules>(
  id: string,
  service: ABTestService<R>,
): Record<string, string> {
  const variantsMap = service.getAllUserVariants(id, navigator.userAgent);
  const result: Record<string, string> = {};
  for (const [experiment, variant] of Object.entries(variantsMap)) {
    result[`experiment${pascalCase(experiment)}`] = variant;
  }
  return result;
}
